import moment from "moment";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { humanizeAuditAction, humanizeEntityType } from "../../helpers";
import { AdminBadge } from "../AdminBadge";
import { EntityPreview } from "../EntityPreview";
import { JsonDiff } from "../JsonDiff";
import { AuditLogDetailContentProps } from "./AuditLogDetailContent.types";

import styles from "./AuditLogDetailContent.module.scss";

const AuditLogDetailContent = ({ auditLog }: AuditLogDetailContentProps) => {
  const { t } = useTranslation();

  const {
    adminName,
    adminId,
    action,
    entityType,
    entityId,
    previousValue,
    newValue,
    ip,
    userAgent,
    requestId,
    createdAt
  } = auditLog;

  const fields: Array<{ label: string; value: ReactNode }> = [
    { label: t("Entity type"), value: humanizeEntityType(entityType) },
    {
      label: t("Entity ID"),
      value: entityId ? <code>{entityId}</code> : "—"
    },
    { label: t("Admin ID"), value: <code>{adminId}</code> },
    { label: t("IP address"), value: ip || "—" },
    {
      label: t("Request ID"),
      value: requestId ? <code>{requestId}</code> : "—"
    },
    {
      label: t("Date & time"),
      value: moment(createdAt).format("DD/MM/YYYY HH:mm:ss")
    },
    { label: t("User agent"), value: userAgent || "—" }
  ];

  const hasChanges = previousValue !== null || newValue !== null;

  return (
    <div className={styles.detail}>
      <div className={styles.detail__header}>
        <AdminBadge adminId={adminId} adminName={adminName} />

        <div className={styles.detail__action}>
          <span className={styles.detail__actionLabel}>
            {humanizeAuditAction(action)}
          </span>
          <code className={styles.detail__actionRaw}>{action}</code>
        </div>

        <span className={styles.detail__time}>
          {moment(createdAt).format("DD/MM/YYYY HH:mm")}
        </span>
      </div>

      {entityType && entityId && (
        <div className={styles.detail__entity}>
          <span className={styles.detail__label}>{t("Entity")}</span>
          <div className={styles.detail__entityRow}>
            <EntityPreview
              entityType={entityType}
              entityId={entityId}
              snapshot={newValue ?? previousValue}
              linkToEntity
            />
            <Link
              className={styles.detail__historyLink}
              to={`/activity-log?entityType=${entityType}&entityId=${entityId}`}
            >
              {t("Show all activity for this entity")}
            </Link>
          </div>
        </div>
      )}

      <div className={styles.detail__fields}>
        {fields.map(({ label, value }) => (
          <div key={label} className={styles.detail__field}>
            <span className={styles.detail__label}>{label}</span>
            <span className={styles.detail__value}>{value}</span>
          </div>
        ))}
      </div>

      <div className={styles.detail__changes}>
        <h4 className={styles.detail__sectionTitle}>
          {previousValue !== null ? t("Changes") : t("Request body")}
        </h4>

        {hasChanges ? (
          <JsonDiff previousValue={previousValue} newValue={newValue} />
        ) : (
          <p className={styles.detail__empty}>
            {t("This row carries no value snapshot.")}
          </p>
        )}
      </div>
    </div>
  );
};

export default AuditLogDetailContent;
